import { Controller } from "@hotwired/stimulus"

// Shared base for the server-side DataTables that render a card per row, are driven
// by a sibling "*-filter" controller, and carry a bulk-selection checkbox column
// (observations, appreciations, action items).
//
// Subclasses provide:
//   - `filterWrapperName`  the data-controller name of the surrounding filter wrapper
//   - `checkboxPrefix`     the shared prefix of the row/select-all/button/input selectors
//   - `createdRow(row, data, index)` the per-row markup
//   - `applyFilters(detail)` (optional) reads filter fields beyond date_from/date_to
//   - `ajaxData(d)` (optional) adds query params beyond date_from/date_to
export default class extends Controller {
  static targets = ["table"]

  datatableInstance = null
  dateFrom = ""
  dateTo = ""

  connect() {
    this.disableTurboCache()
    this.initializeOrRefreshDataTable()
    this.boundCleanup = this.cleanup.bind(this)
    this.boundHandleFiltersChanged = this.handleFiltersChanged.bind(this)
    document.addEventListener("turbo:before-cache", this.boundCleanup)

    this.filterWrapper = this.element.closest(`[data-controller~="${this.filterWrapperName}"]`)
    if (this.filterWrapper) {
      this.filterWrapper.addEventListener(`${this.filterWrapperName}:filtersChanged`, this.boundHandleFiltersChanged)
    }
  }

  disconnect() {
    this.cleanup()
    document.removeEventListener("turbo:before-cache", this.boundCleanup)

    if (this.filterWrapper) {
      this.filterWrapper.removeEventListener(`${this.filterWrapperName}:filtersChanged`, this.boundHandleFiltersChanged)
    }
  }

  handleFiltersChanged(event) {
    this.applyFilters(event.detail)
    this.dateFrom = event.detail.dateFrom || ""
    this.dateTo = event.detail.dateTo || ""
    this.reloadDataTable()
  }

  applyFilters(_detail) {}

  reloadDataTable() {
    const dataTable = $(this.tableTarget).DataTable()
    if (dataTable) {
      dataTable.ajax.reload()
    }
  }

  // Les DataTables serverSide ne survivent pas au cache Turbo (au retour arrière,
  // le snapshot contient une table détruite → "incorrect column count" ou table
  // vide). On désactive le cache Turbo pour toute page contenant une DataTable :
  // le retour arrière refait un fetch serveur propre et la table se ré-initialise
  // normalement. La balise est portée par la page courante uniquement.
  disableTurboCache() {
    if (!document.querySelector('meta[name="turbo-cache-control"]')) {
      const meta = document.createElement("meta")
      meta.name = "turbo-cache-control"
      meta.content = "no-cache"
      document.head.appendChild(meta)
    }
  }

  initializeOrRefreshDataTable() {
    if (this.hasTableTarget) {
      if (this.datatableInstance) {
        this.cleanup()
      }
      this.initDataTable()
    }
  }

  initDataTable() {
    if ($.fn.DataTable.isDataTable(this.tableTarget)) {
      $(this.tableTarget).DataTable().destroy()
    }

    this.datatableInstance = $(this.tableTarget).DataTable({
      processing: true,
      serverSide: true,
      filter: true,
      orderMulti: false,
      ajax: {
        url: this.data.get("source"),
        type: "GET",
        data: (d) => this.ajaxData(d)
      },
      columns: this.columns,
      order: [[0, "desc"]],
      dom: '<"row"<"col-md-6"><"col-md-6"f>>rt<"row"<"col-md-12"ip>>',
      displayLength: 40,
      lengthMenu: [10, 25, 40, 50, 75, 100],
      responsive: true,
      createdRow: (row, data, dataIndex) => this.createdRow(row, data, dataIndex),
      drawCallback: () => this.handleCheckboxes(),
      ...window.datatablesDefaults
    })
  }

  ajaxData(d) {
    d.date_from = this.dateFrom
    d.date_to = this.dateTo
    return d
  }

  // Default columns (legacy array-of-values rows). Subclasses that return
  // server-rendered HTML rows override this with their own column set.
  get columns() {
    return [
      { data: "0", visible: false, searchable: true },
      { data: "1", visible: false, searchable: true },
      { data: "2", visible: false, searchable: true },
      { data: "3", visible: false, searchable: true },
      { data: "4", visible: false, searchable: true }
    ]
  }

  get checkboxSelector() { return `.${this.checkboxPrefix}-checkbox` }
  get selectAllSelector() { return `#${this.checkboxPrefix}-checkboxes` }
  get bulkButtonSelector() { return `.${this.checkboxPrefix}-btn` }
  get bulkInputSelector() { return `.${this.checkboxPrefix}-input` }

  handleCheckboxes() {
    $(this.tableTarget).on("change", this.checkboxSelector, this.handleCheckboxChange.bind(this))
    $(this.selectAllSelector).on("change", this.handleSelectAllChange.bind(this))
  }

  handleCheckboxChange(_event) {
    this.toggleDisabledClass()
  }

  handleSelectAllChange(event) {
    const isChecked = event.target.checked
    $(this.checkboxSelector).prop("checked", isChecked)
    this.toggleDisabledClass()
  }

  toggleDisabledClass() {
    const checkedBoxes = $(this.checkboxSelector).filter(":checked")
    const btn = $(this.bulkButtonSelector)
    const input = $(this.bulkInputSelector)

    const checkIds = checkedBoxes.map(function() {
      return $(this).data("id")
    }).get()

    btn.toggleClass("is-disabled", checkIds.length === 0)

    input.val(checkIds.toString())
  }

  cleanup() {
    if (this.datatableInstance) {
      this.datatableInstance.destroy(true)
      this.datatableInstance = null
    }
    if (this.hasTableTarget) {
      $(this.tableTarget).off("change", this.checkboxSelector)
    }
    $(this.selectAllSelector).off("change")
  }
}
